// GscKiosk.cshtml (QOR staff): a kiosk dispensing and loading a physical card, against the real kiosk API (/kiosk/v1,
// as a kiosk device of the card's program, with its own client credentials), with every HTTP exchange shown for the
// client's IT people: the token, the card number traded for a card token, then the call itself. A kiosk batch card is
// named by its card reference number instead (docs/KIOSK_CARD_STOCK.md): the token, activate, load — two calls.
import { useState, type FormEvent } from 'react';
import { adminApi, type KioskExchange } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { PageHeading } from '../components/ui';
import { isCardNumber, kioskProblem, parseKioskStatus } from '../kiosk';

const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function GscKioskPage() {
  usePageTitle('GSC Kiosk Simulator');
  const [form, setForm] = useState({ cardNumber: '', amount: '100', fee: '3' });
  const [byReference, setByReference] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calls, setCalls] = useState<KioskExchange[]>([]);
  const [card, setCard] = useState<{ status: string | null; balance: number | null } | null>(null);
  const [busy, setBusy] = useState(false);

  function showStatus(exchanges: KioskExchange[]) {
    const last = exchanges.at(-1);
    const parsed = last ? parseKioskStatus(last) : { found: false, status: null, balance: null };
    setCard(parsed.found ? parsed : null);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const which = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('value') ?? 'Submit';
    const cardNumber = form.cardNumber;
    setCalls([]);
    setCard(null);
    if (byReference) {
      const cardReference = cardNumber.trim();
      if (!cardReference) return setError('Enter the card reference number.');
      const problem = kioskProblem('0000000000000', Number(form.amount), Number(form.fee));
      if (problem) return setError(problem);
      setError(null);
      setBusy(true);
      try {
        const activate = await adminApi.kiosk('activate', { cardReference });
        if (!activate.every((c) => c.success) || activate.length < 2) return setCalls(activate);
        const load = await adminApi.kiosk('load', {
          cardReference,
          amount: Number(form.amount),
          fee: Number(form.fee),
        });
        setCalls([...activate, ...load.slice(1)]);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (which === 'Check') {
      if (!isCardNumber(cardNumber)) return setError('Card number must be 13-19 digits, numbers only.');
    } else {
      const problem = kioskProblem(cardNumber, Number(form.amount), Number(form.fee));
      if (problem) return setError(problem);
    }
    setError(null);
    setBusy(true);
    try {
      if (which === 'Check') {
        const status = await adminApi.kiosk('status', { cardNumber });
        setCalls(status);
        showStatus(status);
        return;
      }
      const activate = await adminApi.kiosk('activate', { cardNumber });
      if (!activate.every((c) => c.success) || activate.length < 3) return setCalls(activate);
      const load = await adminApi.kiosk('load', { cardNumber, amount: Number(form.amount), fee: Number(form.fee) });
      const status = await adminApi.kiosk('status', { cardNumber });
      setCalls([...activate, ...load, ...status]);
      showStatus(status);
    } finally {
      setBusy(false);
    }
  }

  const active = card?.status?.toLowerCase() === 'active';
  return (
    <>
      <PageHeading title="GSC Kiosk Simulator" />

      <div className="qc-panel" style={{ maxWidth: 720 }}>
        <p className="qc-empty" style={{ marginTop: 0 }}>
          This page simulates a customer visiting a kiosk to pick up a physical prepaid card and load money onto it.
          Enter a card number as if the kiosk just dispensed it, choose how much to load, and submit — the card is
          activated and funded right away.
        </p>

        {error && (
          <div className="qc-auth-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void submit(e)} style={{ marginTop: 16 }} noValidate>
          <label className="qc-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              name="ByReference"
              checked={byReference}
              onChange={(e) => {
                setByReference(e.target.checked);
                setForm({ ...form, cardNumber: '' });
              }}
            />
            <span>A kiosk batch card: use its card reference number</span>
          </label>
          <label className="qc-field">
            <span>{byReference ? 'Card reference number' : 'Card number'}</span>
            <input
              name="CardNumber"
              placeholder={byReference ? 'As printed with the card' : 'e.g. 4111000000001234'}
              pattern={byReference ? undefined : '\\d{13,19}'}
              inputMode={byReference ? 'text' : 'numeric'}
              autoComplete="off"
              title={byReference ? 'The card reference number' : '13-19 digits, numbers only'}
              required
              value={form.cardNumber}
              onChange={(e) => setForm({ ...form, cardNumber: e.target.value })}
            />
          </label>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label className="qc-field" style={{ maxWidth: 200, flex: '1 1 160px' }}>
              <span>Load amount (USD)</span>
              <input
                name="Amount"
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </label>
            <label className="qc-field" style={{ maxWidth: 200, flex: '1 1 160px' }}>
              <span>Fee (USD)</span>
              <input
                name="Fee"
                type="number"
                step="0.01"
                min="0"
                value={form.fee}
                onChange={(e) => setForm({ ...form, fee: e.target.value })}
              />
            </label>
          </div>
          <p className="qc-empty" style={{ margin: '-8px 0 16px', fontSize: '0.85rem' }}>
            The load amount and fee together can&apos;t exceed $1,000 per card.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button type="submit" value="Submit" className="qor-button qor-button-primary" disabled={busy}>
              Get Card &amp; Load Funds
            </button>
            <button type="submit" value="Check" className="qc-button-ghost" disabled={busy || byReference}>
              Check Balance
            </button>
          </div>
        </form>

        {card && (
          <div
            className="qc-stat-row"
            style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', marginTop: 24, marginBottom: 0 }}
          >
            <div className={`qc-stat-tile ${active ? 'qc-stat-tile--success' : 'qc-stat-tile--red'}`}>
              <span className="qc-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path
                    d="M4 12.5 9 17.5 20 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </span>
              <span className="qc-stat-label">Card Status</span>
              <strong className="qc-stat-value" style={{ fontSize: '1.3rem' }}>
                {card.status}
              </strong>
            </div>
            <div className="qc-stat-tile qc-stat-tile--accent">
              <span className="qc-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" fill="none" />
                  <path
                    d="M12 7v10M9 9.5c0-1.4 1.3-2.5 3-2.5s3 .9 3 2.2-1.3 1.9-3 2.3-3 1-3 2.3 1.3 2.2 3 2.2 3-1.1 3-2.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
              <span className="qc-stat-label">Balance</span>
              <strong className="qc-stat-value" style={{ fontSize: '1.3rem' }}>
                {card.balance === null ? '' : usd(card.balance)} USD
              </strong>
            </div>
          </div>
        )}
      </div>

      {calls.length > 0 && (
        <div className="qc-panel" style={{ maxWidth: 900, marginTop: 20 }}>
          <div className="qc-panel-title">
            <h3>API calls made</h3>
            <span>
              {calls.length} request{calls.length === 1 ? '' : 's'}
            </span>
          </div>
          {calls.map((call, i) => (
            <div className="qc-call-card" key={i}>
              <div className="qc-call-card-head">
                <span
                  className={`qc-badge ${call.success ? 'qc-badge-success' : 'qc-badge-muted'}`}
                  style={{ fontFamily: 'var(--qc-mono-font, Consolas, monospace)' }}
                >
                  {call.method}
                </span>
                <code>{call.path}</code>
                <span
                  className={`qc-call-card-status ${call.success ? 'qc-call-card-status--ok' : 'qc-call-card-status--err'}`}
                >
                  HTTP {call.statusCode}
                </span>
              </div>
              {call.requestJson.trim() !== '' && (
                <>
                  <p className="qc-call-card-label">Request</p>
                  <pre className="qc-call-card-json">
                    <code>{call.requestJson}</code>
                  </pre>
                </>
              )}
              <p className="qc-call-card-label">Response</p>
              <pre className="qc-call-card-json">
                <code>{call.responseJson}</code>
              </pre>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
